import CondolencesIcon from '@/assets/CondolencesIcon'
import FsnNetworkIcon from '@/assets/FsnNetworkIcon'
import { Badge, type BadgeProps, type BadgeVariant } from '@/components/ui/badge'
import { CartSource } from '@/lib/enums/CartSource'
import { TransactionSource } from '@/lib/enums/TransactionSource'
import { cn, readProperty } from '@/lib/utils'
import type { OrderSourceLink } from '@/lib/services/sales-orders/sales-orders.types'
import type { ComponentProps, ComponentType, ReactElement } from 'react'
import { Repeat2 } from 'lucide-react'

interface SalesOrderSourceMeta {
  label: string
  variant: BadgeVariant
  icon?: ComponentType<Pick<ComponentProps<'span'>, 'className' | 'aria-hidden'>>
}

type SourceLinkInput = Pick<Partial<OrderSourceLink>, 'source_system' | 'metadata'>

interface OrderSourceBadgeProps {
  source?: string | null
  sourceLink?: SourceLinkInput | null
  size?: BadgeProps['size']
  className?: string
}

const salesOrderSourceMeta: Partial<Record<string, SalesOrderSourceMeta>> = {
  [CartSource.Web.Value]: {
    label: CartSource.Web.Name,
    variant: 'info',
  },
  [CartSource.POS.Value]: {
    label: CartSource.POS.Name,
    variant: 'default',
  },
  [TransactionSource.External.Value]: {
    label: TransactionSource.External.Name,
    variant: 'secondary',
  },
  [TransactionSource.Internal.Value]: {
    label: TransactionSource.Internal.Name,
    variant: 'muted',
  },
}

const fsnNetworkSourceMeta: Record<'incoming' | 'outgoing' | 'unknown', SalesOrderSourceMeta> = {
  incoming: {
    label: 'Wire-In',
    variant: 'info',
    icon: FsnNetworkIcon,
  },
  outgoing: {
    label: 'Wire-Out',
    variant: 'brand',
    icon: FsnNetworkIcon,
  },
  unknown: {
    label: 'Flower Shop Network',
    variant: 'secondary',
    icon: FsnNetworkIcon,
  },
}

const condolencesSourceMeta: SalesOrderSourceMeta = {
  label: 'Condolences',
  variant: 'brand',
  icon: CondolencesIcon,
}

const flowerSubscriptionSourceMeta: SalesOrderSourceMeta = {
  label: 'Subscription',
  variant: 'brand',
  icon: Repeat2,
}

const unknownSalesOrderSourceMeta: SalesOrderSourceMeta = {
  label: 'Unknown',
  variant: 'muted',
}

export function getSalesOrderSourceMeta(
  source?: string | null,
  sourceLink: SourceLinkInput | null = null
): SalesOrderSourceMeta {
  if (sourceLink?.source_system === 'fsn_network') {
    const direction = readProperty(sourceLink.metadata, 'direction')

    return direction === 'incoming' || direction === 'outgoing'
      ? fsnNetworkSourceMeta[direction]
      : fsnNetworkSourceMeta.unknown
  }

  if (sourceLink?.source_system === 'condolences') {
    return condolencesSourceMeta
  }

  if (sourceLink?.source_system === 'tukios_flowers_subscription') {
    return flowerSubscriptionSourceMeta
  }

  return salesOrderSourceMeta[source ?? ''] ?? unknownSalesOrderSourceMeta
}

export function getSalesOrderSourceLabel(
  source?: string | null,
  sourceLink: SourceLinkInput | null = null
): string {
  return getSalesOrderSourceMeta(source, sourceLink).label
}

function OrderSourceBadge({
  source,
  sourceLink = null,
  size = 'md',
  className = '',
}: OrderSourceBadgeProps): ReactElement {
  const meta = getSalesOrderSourceMeta(source, sourceLink)
  const Icon = meta.icon

  return (
    <Badge variant={meta.variant} size={size} className={cn('gap-1.5', className)}>
      {Icon ? <Icon className="size-3 shrink-0" aria-hidden="true" /> : null}
      {meta.label}
    </Badge>
  )
}

export default OrderSourceBadge
